import type { ApolloCache } from '@apollo/client';
import type {
  CompleteMembershipFragment,
  GetAccountUsersQuery,
  GetAccountUsersQueryVariables,
} from '@frameio/graphql-client/generated';
import { GetAccountUsers } from '@frameio/graphql-client/generated';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import { updateQuery } from '../updateQuery';

const updateAccountUsersData = (
  memberships: Array<CompleteMembershipFragment | null>,
  existingData: GetAccountUsersQuery | null
): GetAccountUsersQuery | null => {
  const account = existingData?.account;
  if (!account?.accountUsers?.nodes) return existingData;

  type Data = (typeof account.accountUsers.nodes)[0];
  const nodes = new Map<string, Data>();
  account.accountUsers.nodes.forEach((node) => {
    if (node.id) nodes.set(node.id, node);
  });

  const netNew = new Set<string>();

  memberships.forEach((membership) => {
    const accountUser = membership?.accountUser;
    if (!accountUser?.id) return;

    if (nodes.has(accountUser.id)) {
      const existingNode = nodes.get(accountUser.id) ?? {};
      nodes.set(accountUser.id, {
        ...(existingNode as Data),
        ...accountUser,
      } as Data);
      return;
    }

    netNew.add(accountUser.id);
    nodes.set(
      accountUser.id,
      accountUser as (typeof account.accountUsers.nodes)[0]
    );
  });

  if (!account.unfilteredAccountUsers) return existingData;

  return {
    ...existingData,
    account: {
      ...account,
      accountUsers: {
        ...account.accountUsers,
        nodes: Array.from(nodes.values()),
        totalCount: account.accountUsers.totalCount + netNew.size,
        pageInfo: account.accountUsers.pageInfo,
      },
      unfilteredAccountUsers: {
        ...account.unfilteredAccountUsers,
        totalCount: account.unfilteredAccountUsers.totalCount + netNew.size,
      },
    },
  };
};

export const updateAccountUsersCache = (
  cache: ApolloCache<any>,
  memberships: Array<CompleteMembershipFragment | null>,
  accountId: string
) => {
  updateQuery<GetAccountUsersQuery, GetAccountUsersQueryVariables>(
    cache,
    {
      query: GetAccountUsers,
      variables: {
        accountId,
        orderBy: [
          {
            field: 'NAME',
            direction: 'ASC_NULLS_LAST',
          },
        ],
        page: {
          first: DEFAULT_INITIAL_PAGE_SIZE,
        },
      },
    },
    (existingData) => updateAccountUsersData(memberships, existingData)
  );
};
